<template>
  <main class="container p-4 pt-2 text-sp-primary">
    <Search />
    <section class="container p-0 my-8 mt-0 text-sp-primary">
      <div v-if="loading" class="fade"><Loading /></div>
      <div v-else-if="error">{{ $t("error") }}</div>
      <div v-else>
        <div class="flex justify-center">
          <h1 class="heading inline-block text-2xl text-sp-accent text-center font-bold">
            {{ categoryName }}
          </h1>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-14 mt-6">
          <div
            v-for="(section, index) in sections"
            :key="section.id"
            class="p-4 px-0 text-center"
          >
            <RouterLink
              :to="`/category/${categoryId}/section/${section.id}`"
              @click="logClick(`category.${categoryId}/${section.id}`, 'category.categoryDetails')"
              class="
                heading justify-center inline-block text-xl text-sp-accent items-center gap-3 
                hover:text-sp-accent duration-150 ease-in-out"
            >
              <p class="leading-tight line-clamp-1 font-bold">
                {{ section.name || "Untitled section" }}
              </p>
            </RouterLink>
            <div class="section-list">
              <RouterLink
                v-for="article in ((expandedSections[section.id] || false) ? section.articles : section.articles.slice(0, 5))"
                :key="article.id"
                :to="`/category/${article.categoryId}/section/${article.sectionId}/article/${article.id}`"
                @click="logClick(`category.${article.categoryId}/${article.sectionId}/${article.id}`, 'category.categoryDetails')"
                class="flex items-center gap-3 p-4 px-0 hover:text-sp-accent duration-150 ease-in-out"
              >
                <img 
                  :src="article.promoted ? magentaIcon : yellowIcon" alt="dot icon" 
                  class="w-3 h-3" 
                />
                <p class="leading-tight line-clamp-1 m-0 p-0 truncate">
                  {{ article.title || 'Untitled article' }}
                </p>
                <img src="../assets/icon-arrow.png" alt="arrow icon" class="w-3 ml-auto" />
              </RouterLink>
              <div 
                v-if="section.articles.length > 5 && !expandedSections[section.id]"
                class="show-all-btn flex w-full mt-1"
              >
                <button
                  @click="expandedSections[section.id] = true"
                  class="underline underline-offset-2 
                    hover:text-sp-accent duration-150 ease-in-out ml-auto py-2"
                >
                  {{ $t("showAll") }} ({{ section.articles.length - 5 }})
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </main>
</template>

<script>
import { ref, onMounted, computed } from "vue"
import { useRoute } from 'vue-router'
import { getUserData } from '../modules/collectData.js'
import { useLogger } from '../composables/useLogger'
import { useHelpCenterFetch } from '../composables/useHelpCenterFetch'
import { useHelpCenterStore } from '../stores/helpCenter'
import Search from '../components/Search.vue'
import Loading from '../components/Loading.vue'
import yellowIcon from '../assets/icon-dot-yellow.png'
import magentaIcon from '../assets/icon-dot-magenta.png'


export default {
  components: {
    Search,
    Loading
  },
  setup() {
    const { lang } = getUserData()
    const { logClick } = useLogger()
    const route = useRoute()
    const helpCenter = useHelpCenterStore()

    const categoryId = computed(() => route.params.categoryId)
    const expandedSections = ref({})

    const { data, loading, error, load } = useHelpCenterFetch(
      () => `helpcenter/${lang}/categories/${route.params.categoryId}`,
      {
        select: (answer) => {
          if (!Array.isArray(answer)) throw new Error('expected array of sections')
          return {
            categoryName: answer[0]?.categoryName ?? null,
            sections: answer.map(section => ({
              name: section.title || 'Untitled section',
              id: section.id || 'No id available',
              promoted: section.promoted === true,
              articles: section.articles || [],
            }))
          }
        }
      }
    )

    const sections = computed(() => data.value?.sections || [])
    const categoryName = computed(() => data.value?.categoryName || null)

    onMounted(async () => {
      await load()
      if (!data.value) return

      const newExpanded = {}
      data.value.sections.forEach(section => {
        newExpanded[section.id] = false
      })
      expandedSections.value = newExpanded

      if (data.value.categoryName) {
        helpCenter.setCurrentCategory({
          id: categoryId.value,
          name: data.value.categoryName,
        })
      }
    })

    return {
      sections,
      expandedSections,
      loading,
      error,
      categoryName,
      categoryId,
      yellowIcon,
      magentaIcon,
      logClick
    }
  }
}
</script>

<style>
.category-list > *,
.section-list > * {
  position: relative;
  background-image: url('../assets/header-line.png');
  background-repeat: no-repeat;
	background-position: center 100%;
  background-size: 100% 20%;	
}
.show-all-btn {
  background: none;
}
</style>